import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { QueryParams } from '@/model/query-params';
import { AccountService } from '../instances/account';
import { AccountConverter } from '@/converters/account';
import { Account } from '@/model';

export const useAccount = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ACCOUNT', params],
    async () => {
      const [err, data] = await AccountService.getAccount(params);
      if (err) {
        throw err;
      }
      return {
        data: AccountConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useAccountById = (id: string) => {
  const query = useQuery(
    ['GET_ACCOUNT_BY_ID', id],
    async () => {
      if (!id) {
        throw new Error('ID inválido');
      }
      const [err, data] = await AccountService.getAccountById(id);
      if (err) {
        throw err;
      }
      return AccountConverter.toDomainFromGetAccountByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetAccountById = () => {
  return useMutation({
    mutationKey: ['GET_ACCOUNT_BY_ID'],
    mutationFn: async (id: string) => {
      const [err, data] = await AccountService.getAccountById(id);
      if (err) {
        throw err;
      }
      return AccountConverter.toDomainFromGetAccountByIdResponse(data);
    },
  });
};

export const usePutAccountDeleteMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_DELETE'],
    mutationFn: async (id: string) => {
      const [err, data] = await AccountService.putAccountDelete(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const usePutAccountAvatarMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_AVATAR'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await AccountService.putAccountAvatar(id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const usePutAccountInfoMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_INFO'],
    mutationFn: async ({ id, payload }: any) => {
      const [err] = await AccountService.putAccountInfo(id, payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePutAccountAddressMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_ADDRESS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err] = await AccountService.putAccountAddress(id, payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePutAccountContactMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_CONTACT'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await AccountService.putAccountContact(id, payload);
      if (err) {
        throw err;
      }
      return AccountConverter.toDomainFromPutAccountContactResponse(data);
    },
  });
};

export const useFetchAccountById = () => {
  const queryClient = useQueryClient();
  const [data, setData] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchAccountById = useCallback(
    async (id: string) => {
      if (!id) {
        setIsError(true);
        throw new Error('ID inválido');
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const [err, rawData] = await AccountService.getAccountById(id);
        if (err) {
          throw err;
        }
        const account = AccountConverter.toDomainFromGetAccountByIdResponse(rawData);
        setData(account);
        queryClient.setQueryData(['GET_ACCOUNT_BY_ID', id], account);
      } catch (error) {
        console.error('Erro ao buscar conta:', error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient],
  );
  const forceRefetch = useCallback(
    (id: string) => {
      queryClient.removeQueries(['GET_ACCOUNT_BY_ID', id]);
      fetchAccountById(id);
    },
    [fetchAccountById, queryClient],
  );
  return {
    data,
    isLoading,
    isError,
    fetchAccountById,
    forceRefetch,
  };
};

export const useAccountNotification = (id: string, params?: QueryParams, enabled?: boolean) => {
  const query = useQuery(
    ['GET_ACCOUNT_NOTIFICATION', params],
    async () => {
      const [err, data] = await AccountService.getAccountNotification(id, params);
      if (err) {
        throw err;
      }
      return AccountConverter.toDomainFromGetAccountNotificationAccountResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutAccountNotificationReadMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_NOTIFICATION_READ'],
    mutationFn: async ({ id }: any) => {
      const [err] = await AccountService.putAccountNotificationRead(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutAccountNotificationAllReadMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_NOTIFICATION_ALL_READ'],
    mutationFn: async ({ id }: any) => {
      const [err] = await AccountService.putAccountNotificationAllRead(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutAccountNotificationCleanMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ACCOUNT_NOTIFICATION_CLEAN'],
    mutationFn: async ({ id }: any) => {
      const [err] = await AccountService.putAccountNotificationClean(id);
      if (err) {
        throw err;
      }
    },
  });
};
